import { useTranslation } from 'react-i18next';

import { Icons } from '@/shared/ui';

import styles from './StateTag.module.css';
import type { CalendarEvent } from './model';
import { isArchived, statusLabel, statusOf } from './status';

/**
 * The state of a listed item as small labelled tags: "Completed" (explicit), "Overdue", "Now" or
 * "Past" (derived from the clock, only for events) and, apart from those, "Archived" (the task is
 * not on the board any more). Nothing for an item that is simply upcoming.
 */
export function StateTag({ event }: { event: CalendarEvent }) {
  const { t } = useTranslation();
  const status = statusOf(event);
  const archived = isArchived(event);
  const archivedTag = archived && (
    <span className={styles.tag} data-status="archived" data-testid="archived-tag">
      <Icons.archive /> {t('calendar.archivedTag')}
    </span>
  );
  if (!status || status === 'upcoming') return archivedTag || null;
  // Completion is shown by the struck-through title only; the word stays for assistive technology.
  if (status === 'completed') {
    return (
      <>
        <span className={styles.srOnly} data-status={status} data-testid="state-tag">
          {statusLabel(status, t)}
        </span>
        {archivedTag}
      </>
    );
  }
  return (
    <>
      <span className={styles.tag} data-status={status} data-testid="state-tag">
        {statusLabel(status, t)}
      </span>
      {archivedTag}
    </>
  );
}
